import { z } from 'zod';
import { optionalText, uuidSchema } from './common.js';

/**
 * Branding and numbering for the documents a tenant sends out.
 *
 * One admin screen owns all of it: what a quote PDF says about the business,
 * and what its number looks like.
 */

/**
 * A document-number prefix.
 *
 * No trailing digits: the sequence is read back as the trailing run of digits
 * in the stored number, so `INV2026` would make `INV2026-00007` ambiguous with
 * the counter itself. Letters, digits and dashes otherwise, because `INV-UK`
 * and `Q2` are both reasonable and neither confuses the reader.
 */
export const numberPrefixSchema = z
  .string()
  .trim()
  .min(1, 'Required')
  .max(12, 'Keep it short — it sits in front of every number')
  .regex(/^[A-Za-z0-9-]+$/u, 'Letters, digits and dashes only')
  .regex(/[^0-9]$/u, 'Cannot end with a digit, or the number would run into it');

const hexColour = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/u, 'Use a 6-digit hex colour, like #1f2937');

/**
 * A logo, as a URL.
 *
 * `https:` or a `data:` image. Not `http:` — a PDF that fetches its logo over
 * plain HTTP leaks who is reading it — and not `javascript:` or anything else
 * that would be a rendering surface rather than a picture.
 */
export const logoUrlSchema = z
  .string()
  .trim()
  .max(2_000_000)
  .refine(
    (value) =>
      value.startsWith('https://') || /^data:image\/(png|jpeg|gif|webp|svg\+xml);/u.test(value),
    'Use an https:// address or a data: image',
  );

export const updateBillingSettingsSchema = z.object({
  companyName: optionalText(200),
  addressLines: optionalText(500),
  email: z
    .union([z.email(), z.literal('')])
    .transform((v) => (v === '' ? null : v))
    .nullish(),
  phone: optionalText(60),
  website: optionalText(300),
  taxId: optionalText(60),
  taxIdLabel: z.string().trim().min(1).max(40).optional(),
  logoUrl: z
    .union([logoUrlSchema, z.literal('')])
    .transform((v) => (v === '' ? null : v))
    .nullish(),
  accentColor: hexColour.optional(),

  quoteTerms: optionalText(8000),
  invoiceTerms: optionalText(8000),
  creditNoteTerms: optionalText(8000),
  footerNote: optionalText(2000),

  quotePrefix: numberPrefixSchema.optional(),
  salesOrderPrefix: numberPrefixSchema.optional(),
  invoicePrefix: numberPrefixSchema.optional(),
  creditNotePrefix: numberPrefixSchema.optional(),
  purchaseOrderPrefix: numberPrefixSchema.optional(),
  expenseReportPrefix: numberPrefixSchema.optional(),
  gstInvoicePrefix: numberPrefixSchema.optional(),
  numberWidth: z.coerce.number().int().min(1).max(12).optional(),

  /** Email the admins (and the list below) a summary whenever an invoice is changed. */
  invoiceSecurityNotificationsEnabled: z.coerce.boolean().optional(),
  /** Recipients beyond the organisation's admins. The admins are always included. */
  invoiceSecurityNotificationEmails: z.array(z.email()).max(50).optional(),
});
export type UpdateBillingSettingsInput = z.infer<typeof updateBillingSettingsSchema>;

export interface BillingSettingsView {
  companyName: string | null;
  addressLines: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  taxId: string | null;
  taxIdLabel: string;
  logoUrl: string | null;
  accentColor: string;
  quoteTerms: string | null;
  invoiceTerms: string | null;
  creditNoteTerms: string | null;
  footerNote: string | null;
  quotePrefix: string;
  salesOrderPrefix: string;
  invoicePrefix: string;
  creditNotePrefix: string;
  purchaseOrderPrefix: string;
  expenseReportPrefix: string;
  gstInvoicePrefix: string;
  numberWidth: number;

  invoiceSecurityNotificationsEnabled: boolean;
  invoiceSecurityNotificationEmails: string[];
}

/**
 * Turning an accepted quote into an invoice.
 *
 * Everything that makes the invoice comes from the quote, so the only inputs
 * are the two things a person decides at that moment: when it is due, and
 * whether accepting it also closes the quote.
 */
export const convertQuoteSchema = z.object({
  invoiceDate: z.iso.date().optional(),
  dueDate: z.iso.date().optional(),
  /** Marks the quote ACCEPTED as part of the same transaction. */
  markQuoteAccepted: z.boolean().default(true),
  /**
   * The PDF layout the invoice is raised with.
   *
   * Omitted, the invoice gets the counterpart of the quote's layout — a quote
   * on "Weddings De Goa — Quote" becomes an invoice on "Weddings De Goa —
   * Invoice" — and, when the quote's layout has none, the organisation's
   * default.
   */
  templateId: uuidSchema.optional(),
});
export type ConvertQuoteInput = z.infer<typeof convertQuoteSchema>;

/**
 * Emailing a quote or invoice to the customer, as a PDF.
 *
 * Every field is optional: the point of the button is that sending the thing
 * takes one click. What is left out is filled in from the document — the
 * linked contact's address, its number in the subject — so the common case
 * needs no input at all.
 */
export const emailDocumentSchema = z.object({
  /** Defaults to the linked contact's email address. */
  to: z.array(z.email()).max(25).optional(),
  cc: z.array(z.email()).max(25).default([]),
  bcc: z.array(z.email()).max(25).default([]),
  subject: z.string().trim().min(1).max(300).optional(),
  bodyHtml: z.string().trim().min(1).max(100_000).optional(),
  /** Recorded on the message, so the timeline says which template was used. */
  templateId: uuidSchema.optional(),
});
export type EmailDocumentInput = z.infer<typeof emailDocumentSchema>;
